import type { LadderPolicyConstraint } from '@/types/api/constraints'

import { AllocationConstraintType } from '@moment/horizon-data-access/proposal'
import { toError } from '@moment/observability/logger'

import { createLogger } from '@/lib/logger'
import {
  expandLadderPolicyConstraint,
  getLadderPeriods,
  getMaturityBasis,
  type LadderRungConstraint,
  resolveLadderReferenceDate,
} from '@/portfolio/proposal-builder/data/transformers/ladder-to-constraints'
import {
  formatBoundMoney,
  formatRuleWeight,
} from '@/portfolio/shared/constraint/format-rule-value'
import {
  decimalToPercentage,
  formatMoney,
  formatPercentage,
} from '@/utils/presentation/formatting'

const LOGGER = createLogger(
  'portfolio/proposal-builder/components/risk-factor-list/helpers/get-ladder-policy-display',
)

export const LADDER_RUNGS_UNAVAILABLE_TEXT = 'Rungs could not be displayed'

interface LadderPolicyRung {
  id: string
  text: string
  /** "0–1y" */
  range: string
  /** "10% (9% – 11%)" or "10% (±$100K)"; null on the fallback rung. */
  value: string | null
}

interface LadderPolicyDisplay {
  /** Basis + bucket width, e.g. "By maturity date · 1-year buckets". */
  summaryText: string
  /**
   * One entry per ladder period, e.g. "0–1y · target 20.0% (10.0%–30.0%)", or
   * "0–1y · target 20.0% (±$100,000)" for a market-value tolerance.
   */
  rungs: LadderPolicyRung[]
}

function formatBucketLabel(periodLength: number): string {
  if (periodLength % 12 === 0) {
    return `${periodLength / 12}-year buckets`
  }
  return `${periodLength}-month buckets`
}

function formatYears(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

function formatPct(weight: number): string {
  return formatPercentage(decimalToPercentage(weight), { numDigits: 1 })
}

export function getLadderPolicySpan(
  ladder: Pick<
    LadderPolicyConstraint,
    'period_length' | 'start_period' | 'end_period' | 'num_periods'
  >,
): string {
  const { start, end } = getLadderPeriods(ladder)
  const startYears = formatYears((start * ladder.period_length) / 12)
  const endYears = formatYears((end * ladder.period_length) / 12)
  return `${startYears} – ${endYears}y, ${formatBucketLabel(ladder.period_length)}`
}

export function getLadderPolicySummary(
  ladder: Pick<LadderPolicyConstraint, 'maturity_option' | 'period_length'>,
): string {
  const basisLabel = getMaturityBasis(ladder).label
  return `By ${basisLabel} · ${formatBucketLabel(ladder.period_length)}`
}

function formatBand(
  minWeight: number | undefined,
  maxWeight: number | undefined,
  marketValueTolerance: number | undefined,
): string {
  if (minWeight !== undefined && maxWeight !== undefined) {
    return ` (${formatPct(minWeight)}–${formatPct(maxWeight)})`
  }
  if (marketValueTolerance != null) {
    return ` (±${formatMoney(marketValueTolerance, { numDigits: 0 })})`
  }
  return ''
}

// Reuse request-time expansion so ladder rungs match backend enforcement. A
// ladder whose rungs cannot be rendered degrades to a placeholder row instead
// of taking down the page that lists it.
export function getLadderPolicyDisplay(
  ladder: LadderPolicyConstraint,
  currency?: string,
): LadderPolicyDisplay {
  const summaryText = getLadderPolicySummary(ladder)
  try {
    return { summaryText, rungs: buildRungs(ladder, currency) }
  } catch (error) {
    LOGGER.error('Failed to build ladder rungs', {
      error: toError(error),
      ladder,
    })
    return {
      summaryText,
      rungs: [
        {
          id: 'rung-unavailable',
          text: LADDER_RUNGS_UNAVAILABLE_TEXT,
          range: LADDER_RUNGS_UNAVAILABLE_TEXT,
          value: null,
        },
      ],
    }
  }
}

function buildRungs(
  ladder: LadderPolicyConstraint,
  currency: string | undefined,
): LadderPolicyRung[] {
  const { start: startPeriod } = getLadderPeriods(ladder)
  return expandLadderPolicyConstraint(
    ladder,
    resolveLadderReferenceDate(ladder),
  )
    .filter(
      (constraint): constraint is LadderRungConstraint =>
        constraint.type === AllocationConstraintType.allocation,
    )
    .map((constraint, index) => {
      // Bucket p spans [p * period_length / 12, (p + 1) * period_length / 12) years.
      const period = startPeriod + index
      const start = formatYears((period * ladder.period_length) / 12)
      const end = formatYears(((period + 1) * ladder.period_length) / 12)

      // The basis is named once in the summary row, so rungs carry only the range.
      const range = `${start}–${end}y`
      const target = ` · target ${formatPct(constraint.target_weight)}`
      const band = formatBand(
        constraint.min_weight,
        constraint.max_weight,
        ladder.market_value_tolerance,
      )

      return {
        id: `rung-${index}`,
        text: `${range}${target}${band}`,
        range,
        value: formatRungValue(
          constraint.target_weight,
          constraint.min_weight,
          constraint.max_weight,
          ladder.market_value_tolerance,
          currency,
        ),
      }
    })
}

function formatRungValue(
  targetWeight: number,
  minWeight: number | undefined,
  maxWeight: number | undefined,
  marketValueTolerance: number | undefined,
  currency: string | undefined,
): string {
  const target = formatRuleWeight(targetWeight)
  if (minWeight !== undefined && maxWeight !== undefined) {
    return `${target} (${formatRuleWeight(minWeight)} – ${formatRuleWeight(maxWeight)})`
  }
  if (marketValueTolerance != null) {
    return `${target} (±${formatBoundMoney(marketValueTolerance, currency)})`
  }
  return target
}
